import React, { useEffect, useState } from 'react';
import DocViewer, { DocViewerRenderers } from "@cyntler/react-doc-viewer";
import PageForm from "components/pageForm";
import SubNavbar from "components/SubNavbar/SubNavbar";
import { ArrowLeft, DocumentIcon } from "assets/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import UseContext from "hooks/useContext";
import Button from "components/button";
import ApiDatabase from "server";

const DownloadIcon = ({ wh, color }) => (
    <svg
        width={wh}
        height={wh}
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15M7 10L12 15M12 15L17 10M12 15V3"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const InvoiceFiche = () => {
    const { companyData } = UseContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    
    const invoiceId = searchParams.get("id");
    const fileName = searchParams.get("fileName");
    const dateFacture = searchParams.get("date");
    const amount = searchParams.get("amount");
    const status = searchParams.get("status") === "true";
    const returnId = searchParams.get("returnId");
    
    const token = localStorage.getItem("xsrfToken");
    const [docs, setDocs] = useState([]);
    const [isWaiting, setIsWaiting] = useState(true);

    const handleBack = () => {
        if (returnId) {
            navigate(`/invoice?selectedId=${returnId}`);
        } else {
            navigate(-1);
        }
    };

    useEffect(() => {
        if (token && companyData._id && fileName) {
            loadPdf();
        }
    }, [token, companyData._id, fileName]);

    const loadPdf = () => {
        setIsWaiting(true);
        
        ApiDatabase.postPresignFiles(
            {
                token,
                type: "invoice",
                idCompany: companyData._id,
                fileName,
            },
            (data) => {
                const urls = Array.isArray(data?.urls) ? data.urls : [];
                const docsFromLinks = urls.filter(Boolean).map((item) => {
                    const href = item.url || item.uri || "";
                    let originalFileName = "";
                    try {
                        const u = new URL(href);
                        originalFileName = decodeURIComponent(
                            (u.pathname.split("/").pop() || "").trim()
                        );
                    } catch (e) {
                        const clean = href.split("?")[0].split("#")[0];
                        originalFileName = decodeURIComponent(
                            (clean.split("/").pop() || "").trim()
                        );
                    }
                    return {
                        uri: href,
                        fileName: originalFileName || "document",
                        fileType: item.type || "application/pdf",
                    };
                });
                setIsWaiting(false);
                setDocs(docsFromLinks);
            },
            (err) => {
                console.log(err);
                setDocs([]);
                setIsWaiting(false);
            }
        );
    };

    const handleDownload = () => {
        if (!token || !companyData?._id || !fileName) return;
        ApiDatabase.postPresignFiles(
            {
                token,
                type: "invoice",
                idCompany: companyData._id,
                fileName,
                download: true,
            },
            (data) => {
                const urls = Array.isArray(data?.urls) ? data.urls : [];
                const url = urls[0]?.url || urls[0]?.uri;
                if (url) {
                    window.open(url, '_blank');
                }
            },
            (err) => console.log(err)
        );
    };

    return (
        <PageForm>
            <SubNavbar>
                <div className="w-full flex items-center justify-between h-full">
                    <div className="flex items-center gap-4">
                        <Button size={'SM'} color={'SECONDARY'} onClick={handleBack}>
                            <div className="flex items-center gap-2">
                                <ArrowLeft w={15} h={12} color={'#374151'}/>
                                <span>Retour</span>
                            </div>
                        </Button>
                    </div>
                    {docs.length > 0 && (
                        <Button size={'SM'} color={'SECONDARY'} onClick={handleDownload}>
                            <div className="flex items-center gap-2">
                                <DownloadIcon wh={18} color={"#374151"} />
                                <span>Télécharger</span>
                            </div>
                        </Button>
                    )}
                </div>
            </SubNavbar>

            <div className="col-span-12 mt-6 px-6">
                {/* Visualiseur PDF */}
                <div className="col-span-12">
                    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" style={{ minHeight: '80vh' }}>
                        {isWaiting ? (
                            <div className="flex flex-col p-6 animate-pulse space-y-6 h-full">
                                <div className="flex items-center space-x-4">
                                    <div className="h-20 w-20 bg-gray-200 rounded"></div>
                                    <div className="flex-1 space-y-3">
                                        <div className="h-5 w-1/3 bg-gray-200 rounded"></div>
                                        <div className="h-4 w-1/4 bg-gray-200 rounded"></div>
                                    </div>
                                </div>
                                <div className="space-y-2 flex-1">
                                    <div className="h-4 w-1/5 bg-gray-200 rounded"></div>
                                    <div className="h-3 w-2/3 bg-gray-200 rounded"></div>
                                    <div className="h-3 w-3/4 bg-gray-200 rounded"></div>
                                    <div className="h-3 w-1/2 bg-gray-200 rounded"></div>
                                </div>
                            </div>
                        ) : docs.length > 0 ? (
                            <DocViewer
                                style={{ minHeight: '80vh', height: '100%' }}
                                documents={docs}
                                config={{
                                    pdfZoom: {
                                        defaultZoom: 1,
                                        zoomJump: 0.1,
                                    },
                                    header: {
                                        disableHeader: true,
                                    },
                                }}
                                pluginRenderers={DocViewerRenderers}
                            />
                        ) : (
                            <div className="flex flex-col items-center justify-center h-full min-h-[60vh] text-gray-500">
                                <DocumentIcon wh={48} color={"#9CA3AF"} />
                                <p className="mt-4">Aucun document disponible</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </PageForm>
    );
};

export default InvoiceFiche;
